import { Box, Flex, FormControl, Icon, IconButton, Input, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FaCheck, FaLock, FaLockOpen, FaPlus, FaTrash } from "react-icons/fa";
import {
  CustomContractDataFieldDto,
  UpsertCustomContractDataFieldApiArg,
  useDeleteCustomContractDataFieldMutation,
  useUpsertCustomContractDataFieldMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { editCustomField, removeCustomField } from "../../../../common/redux/reducers/authStateReducer";
import { SupplierFilterToggle } from "./SupplierFilterToggle";
import { ToggleInternalField } from "./ToggleInternalField";

export const FieldForm = ({ sectionId, field }: { sectionId: string; field: CustomContractDataFieldDto }) => {
  const dispatch = useAppDispatch();
  const authState = useLoggedInWithOrgContextState();
  const displayer = useApiError();

  const [upsertField] = useUpsertCustomContractDataFieldMutation();
  const [deleteField, { isLoading: isDeleting }] = useDeleteCustomContractDataFieldMutation();

  const [newOption, setNewOption] = useState<string>();

  return (
    <Box key={field.id} pb="5">
      <Flex alignItems={"center"} justifyContent={"space-between"} width={"sm"}>
        <Flex alignItems={"center"}>
          <Text fontSize={"sm"} fontWeight={"semibold"} pr="2">
            {field.name}
          </Text>
          <Text fontSize={"sm"}>({field.type})</Text>
        </Flex>
        <IconButton
          aria-label={t("Delete field")}
          size="xs"
          variant={"ghost"}
          colorScheme="gray"
          icon={<Icon as={FaTrash} />}
          isLoading={isDeleting}
          onClick={async () => {
            try {
              await deleteField({
                orgId: authState.selectedOrg.id,
                deleteCustomFieldDto: { sectionId, fieldId: field.id },
              }).unwrap();
              dispatch(removeCustomField({ sectionId, fieldId: field.id }));
            } catch (e) {
              displayer.trigger(e);
            }
          }}
        />
      </Flex>
      <FormControl width="96" alignItems={"center"} py="2">
        <Flex>
          <ToggleInternalField
            label={t("Changing visibility will not affect existing contracts")}
            isChecked={field.isInternal}
            onChange={async (checked: boolean) => {
              const updatedField: CustomContractDataFieldDto = { ...field, isInternal: checked };
              dispatch(
                editCustomField({
                  sectionId,
                  field: updatedField,
                })
              );
              try {
                await upsertField({
                  orgId: authState.selectedOrg.id,
                  upsertCustomContractFieldDto: {
                    sectionId,
                    field: updatedField,
                  },
                } as UpsertCustomContractDataFieldApiArg).unwrap();
              } catch (e) {
                dispatch(editCustomField({ sectionId, field }));
                displayer.trigger(e);
              }
            }}
          />
          {field.isInternal ? (
            <Text fontSize={"xs"} color="smMuted">
              {t("Internal field - will not be visible to contract counterparty")}
              <Icon as={FaLock} boxSize={"3"} ml="1" />
            </Text>
          ) : (
            <Text fontSize={"xs"} color="smMuted">
              {t("Shared field - visible to contract counterparty")}
              <Icon as={FaLockOpen} boxSize={"3"} ml="1" />
            </Text>
          )}
        </Flex>
      </FormControl>
      <SupplierFilterToggle field={field} />
      {(field.type === "CustomEnum" || field.type === "CustomMultiEnum") && (
        <Flex flexDirection={"column"}>
          {field.options?.length ? (
            field.options?.map((option) => (
              <Flex key={option} alignItems={"center"} justifyContent={"space-between"} maxWidth={"xs"} py="1">
                <Text
                  fontSize={"sm"}
                  fontStyle={"italic"}
                  backgroundColor="smBackgroundSecondary"
                  rounded="lg"
                  py="1"
                  px="4"
                >
                  {option}
                </Text>
                <IconButton
                  variant={"ghost"}
                  size={"xs"}
                  aria-label={t("Delete choice")}
                  colorScheme="gray"
                  icon={<Icon as={FaTrash} />}
                  onClick={async () => {
                    const updatedField = { ...field, options: field.options?.filter((o) => o !== option) };
                    dispatch(
                      editCustomField({
                        sectionId,
                        field: updatedField,
                      })
                    );
                    try {
                      await upsertField({
                        orgId: authState.selectedOrg.id,
                        upsertCustomContractFieldDto: {
                          sectionId,
                          field: updatedField,
                        },
                      } as UpsertCustomContractDataFieldApiArg).unwrap();
                    } catch (e) {
                      dispatch(editCustomField({ sectionId, field }));
                      displayer.trigger(e);
                    }
                  }}
                />
              </Flex>
            ))
          ) : (
            <Text fontSize={"xs"} fontStyle={"italic"} py="2">
              {t("No options yet")}
            </Text>
          )}
          {newOption !== undefined && (
            <Flex maxWidth={"xs"}>
              <Input
                size={"xs"}
                rounded="md"
                value={newOption}
                onChange={(e) => {
                  setNewOption(e.target.value);
                }}
              />
              <IconButton
                size={"xs"}
                aria-label={t("Save")}
                variant={"outline"}
                colorScheme="teal"
                icon={<Icon as={FaCheck} />}
                onClick={async () => {
                  const typedOption = newOption;
                  const updatedField = { ...field, options: [...(field.options ?? []), typedOption] };
                  setNewOption(undefined);
                  dispatch(
                    editCustomField({
                      sectionId,
                      field: updatedField,
                    })
                  );
                  try {
                    await upsertField({
                      orgId: authState.selectedOrg.id,
                      upsertCustomContractFieldDto: {
                        sectionId,
                        field: updatedField,
                      },
                    } as UpsertCustomContractDataFieldApiArg).unwrap();
                  } catch (e) {
                    dispatch(editCustomField({ sectionId, field }));
                    setNewOption(typedOption);
                    displayer.trigger(e);
                  }
                }}
              />
            </Flex>
          )}
          <Flex justifyContent={"end"} maxWidth={"xs"}>
            <IconButton
              variant={"ghost"}
              size={"xs"}
              aria-label={t("Add choice")}
              color="teal"
              icon={<Icon as={FaPlus} />}
              onClick={() => setNewOption("")}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
};
